import { AfterViewInit, Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { GeniusChartService } from '../../services/genius-chart/genius-chart.service';
import { VideoService } from '../../services/video-service/video.service';

interface DemoPhase {
  labelKey: string;
  duration: number;
  barValue: number;
  barColor: string;
  videoSpeed: number | null; // null = video paused
  direction: 'reward' | 'inhibit';
}

const RED = '#FF0000';
const GREEN = '#00FF00';
const OSCILLOSCOPE_SAMPLE_INTERVAL = 150;
const OSCILLOSCOPE_MAX_SAMPLES = 20;

// ponytail: scripted timings/values for the demo, not derived from real signal thresholds - tune here if the demo should feel different.
// Each direction gets its own bad (slow/pause, outside range) and good (normal speed) moment, so the direction badge - which only
// shows while outside range - is actually reachable for both 'reward' and 'inhibit', not just one of them.
const DEMO_PHASES: DemoPhase[] = [
  // reward: low signal is bad (below threshold), high signal is good
  { labelKey: 'TRAINING_INTRO_DIALOG.PHASE_REWARD', duration: 4000, barValue: 9, barColor: GREEN, videoSpeed: 1, direction: 'reward' },
  { labelKey: 'TRAINING_INTRO_DIALOG.PHASE_SLOW_DOWN', duration: 4000, barValue: 2, barColor: RED, videoSpeed: 0.5, direction: 'reward' },
  { labelKey: 'TRAINING_INTRO_DIALOG.PHASE_PAUSE', duration: 3000, barValue: 2, barColor: RED, videoSpeed: null, direction: 'reward' },
  // inhibit: high signal is bad (above threshold), low signal is good
  { labelKey: 'TRAINING_INTRO_DIALOG.PHASE_INHIBIT', duration: 4000, barValue: 2, barColor: GREEN, videoSpeed: 1, direction: 'inhibit' },
  { labelKey: 'TRAINING_INTRO_DIALOG.PHASE_SLOW_DOWN', duration: 4000, barValue: 7, barColor: RED, videoSpeed: 0.5, direction: 'inhibit' },
  { labelKey: 'TRAINING_INTRO_DIALOG.PHASE_PAUSE', duration: 3000, barValue: 9, barColor: RED, videoSpeed: null, direction: 'inhibit' },
];

@Component({
  selector: 'kt-training-intro-dialog',
  templateUrl: './training-intro-dialog.component.html',
  styleUrls: ['./training-intro-dialog.component.scss']
})
export class TrainingIntroDialogComponent implements AfterViewInit, OnDestroy {

  @ViewChild('introBarChart') barChartRef: ElementRef;
  @ViewChild('introOscilloscopeChart') oscilloscopeChartRef: ElementRef;
  @ViewChild('introVideo') videoRef: ElementRef<HTMLVideoElement>;

  chart;
  oscilloscopeChart;
  phases = DEMO_PHASES;
  currentPhaseIndex = 0;
  isPaused = false;
  private phaseTimeout: any;
  private oscilloscopeInterval: any;
  private destroyed = false;
  private phaseStartedAt = 0;
  private remainingMs = 0;

  constructor(
    public dialogRef: MatDialogRef<TrainingIntroDialogComponent>,
    private chartService: GeniusChartService,
    private videoService: VideoService
  ) { }

  get currentPhase(): DemoPhase {
    return this.phases[this.currentPhaseIndex];
  }

  get videoStopped(): boolean {
    return this.currentPhase.videoSpeed === null;
  }

  get videoBadgeKey(): string {
    const speed = this.currentPhase.videoSpeed;
    if (speed === null) {
      return 'TRAINING_INTRO_DIALOG.VIDEO_PAUSED';
    }
    return speed < 1 ? 'TRAINING_INTRO_DIALOG.VIDEO_SLOWED' : 'TRAINING_INTRO_DIALOG.VIDEO_NORMAL';
  }

  get directionBadgeKey(): string {
    return this.currentPhase.direction === 'reward'
      ? 'TRAINING_INTRO_DIALOG.DIRECTION_REWARD'
      : 'TRAINING_INTRO_DIALOG.DIRECTION_INHIBIT';
  }

  ngAfterViewInit(): void {
    const barChartCanvas = document.getElementById('introBarChart');
    if (barChartCanvas) {
      this.chart = this.chartService.createTrainingIntroBarChart(barChartCanvas);
      this.chart.update();
    }
    const oscilloscopeCanvas = document.getElementById('introOscilloscopeChart');
    if (oscilloscopeCanvas) {
      this.oscilloscopeChart = this.chartService.createTrainingIntroOscilloscopeChart(oscilloscopeCanvas);
      this.oscilloscopeChart.update();
      this.oscilloscopeInterval = setInterval(() => this.pushOscilloscopeSample(), OSCILLOSCOPE_SAMPLE_INTERVAL);
    }
    this.runPhase(0);
  }

  runPhase(index: number): void {
    if (this.destroyed) {
      return;
    }
    this.currentPhaseIndex = index;
    const phase = this.phases[index];
    this.applyPhase(phase);
    this.startPhaseTimer(phase.duration);
  }

  startPhaseTimer(durationMs: number): void {
    this.remainingMs = durationMs;
    this.phaseStartedAt = Date.now();
    this.phaseTimeout = setTimeout(() => {
      this.runPhase((this.currentPhaseIndex + 1) % this.phases.length);
    }, durationMs);
  }

  togglePause(): void {
    if (this.isPaused) {
      this.resume();
    } else {
      this.pause();
    }
  }

  pause(): void {
    if (this.isPaused || this.destroyed) {
      return;
    }
    this.isPaused = true;
    clearTimeout(this.phaseTimeout);
    clearInterval(this.oscilloscopeInterval);
    this.remainingMs = Math.max(0, this.remainingMs - (Date.now() - this.phaseStartedAt));
    this.videoService.pauseVideo(this.videoRef.nativeElement);
  }

  resume(): void {
    if (!this.isPaused || this.destroyed) {
      return;
    }
    this.isPaused = false;
    const phase = this.currentPhase;
    const video = this.videoRef.nativeElement;
    if (phase.videoSpeed !== null && video.src) {
      this.videoService.playVideo(video);
      this.videoService.setVideoPaybackRate(video, phase.videoSpeed);
    }
    this.startPhaseTimer(this.remainingMs);
    this.oscilloscopeInterval = setInterval(() => this.pushOscilloscopeSample(), OSCILLOSCOPE_SAMPLE_INTERVAL);
  }

  applyPhase(phase: DemoPhase): void {
    if (this.chart) {
      this.chart.data.datasets[0].data = [phase.barValue];
      this.chart.data.datasets[0].backgroundColor = [phase.barColor];
      this.chart.update();
    }
    const video = this.videoRef.nativeElement;
    if (!video.src) {
      return;
    }
    if (phase.videoSpeed === null) {
      this.videoService.pauseVideo(video);
      return;
    }
    if (video.paused) {
      this.videoService.playVideo(video);
    }
    this.videoService.setVideoPaybackRate(video, phase.videoSpeed);
  }

  pushOscilloscopeSample(): void {
    if (!this.oscilloscopeChart) {
      return;
    }
    const phase = this.currentPhase;
    const jitter = (Math.random() - 0.5) * 0.8;
    const sample = Math.min(9, Math.max(0, phase.barValue + jitter));
    const data = this.oscilloscopeChart.data.datasets[0].data;
    data.push(+sample.toFixed(2));
    this.oscilloscopeChart.data.labels.push('');
    if (data.length > OSCILLOSCOPE_MAX_SAMPLES) {
      data.shift();
      this.oscilloscopeChart.data.labels.shift();
    }
    this.oscilloscopeChart.data.datasets[0].borderColor = phase.barColor;
    this.oscilloscopeChart.update();
  }

  close() {
    this.dialogRef.close(true);
  }

  cancel() {
    this.dialogRef.close(false);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.phaseTimeout) {
      clearTimeout(this.phaseTimeout);
    }
    if (this.oscilloscopeInterval) {
      clearInterval(this.oscilloscopeInterval);
    }
  }

}
